import { useState, useEffect } from 'react';
import { Badge } from '../../components/ui/Badge';
import { Check, Plus, X } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import {
  useCreateIntegration,
  useUpdateIntegration,
  useDeleteIntegration,
} from '../../hooks/useIntegrations';
import { Textarea } from '../../components/ui/Input';
import { PROVIDERS } from '@sequesto-os/shared';
import type { Integration } from '../../api/integrations';
import { getBingAdsAuthUrl, getGoogleAdsAuthUrl, verifySlackToken } from '../../api/integrations';

interface AvailableProvider {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface ConfigDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  integration: Integration | null;
  providerId: string | null;
  isCreating: boolean;
  availableProviders?: AvailableProvider[];
}

function getNestedValue(obj: Record<string, unknown>, path: string): string {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[part];
  }
  return (current as string) ?? '';
}

function setNestedValue(obj: Record<string, unknown>, path: string, value: string): Record<string, unknown> {
  const parts = path.split('.');
  const result = { ...obj };

  if (parts.length === 1) {
    result[parts[0]] = value;
    return result;
  }

  const [first, ...rest] = parts;
  const nested = (typeof result[first] === 'object' && result[first] != null)
    ? { ...(result[first] as Record<string, unknown>) }
    : {};
  nested[rest.join('.')] = value;
  result[first] = nested;
  return result;
}

export function ConfigDrawer({
  isOpen,
  onClose,
  integration,
  providerId,
  isCreating,
  availableProviders = [],
}: ConfigDrawerProps) {
  const [name, setName] = useState('');
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [isCustom, setIsCustom] = useState(false);
  const [customProviderId, setCustomProviderId] = useState('');
  const [customFields, setCustomFields] = useState<Array<{ key: string; value: string }>>([{ key: '', value: '' }]);
  const [isConnectingOAuth, setIsConnectingOAuth] = useState(false);
  const [newBotToken, setNewBotToken] = useState('');
  const [isVerifyingBot, setIsVerifyingBot] = useState(false);
  const [botVerifyError, setBotVerifyError] = useState<string | null>(null);

  const createIntegration = useCreateIntegration();
  const updateIntegration = useUpdateIntegration();
  const deleteIntegration = useDeleteIntegration();

  const activeProviderId = providerId ?? selectedProvider;
  const provider = integration?.provider ?? activeProviderId ?? '';
  const fields = PROVIDERS[provider as keyof typeof PROVIDERS]?.fields ?? [];
  const showProviderSelection = isCreating && !activeProviderId && !isCustom;

  useEffect(() => {
    if (integration) {
      setName(integration.name);
      // Strip redacted placeholder values so password fields show empty
      // instead of the literal '••••••••' string (real values are preserved server-side)
      const rawConfig = integration.config as Record<string, unknown>;
      const cleanConfig: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(rawConfig)) {
        if (value !== '••••••••') {
          cleanConfig[key] = value;
        }
      }
      setConfig(cleanConfig);
    } else if (activeProviderId) {
      setName(activeProviderId.charAt(0).toUpperCase() + activeProviderId.slice(1).replace('_', ' '));
      setConfig({});
    }
  }, [integration, activeProviderId]);

  // Calendly is multi-instance. Derive "Calendly Patrick" / "Calendly Andrew" /
  // "Calendly Nadia" from the ownerSlug field while the name is still its
  // generic default, so each row gets a distinguishable name out of the box.
  useEffect(() => {
    if (provider !== 'calendly' || integration) return;
    const ownerSlug = (config.ownerSlug as string | undefined)?.trim().toLowerCase();
    if (!ownerSlug) return;
    const isDefaultName = name === 'Calendly' || /^Calendly( [a-z]+)?$/i.test(name);
    if (!isDefaultName) return;
    setName(`Calendly ${ownerSlug.charAt(0).toUpperCase()}${ownerSlug.slice(1)}`);
  }, [provider, integration, config.ownerSlug, name]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedProvider(null);
      setIsCustom(false);
      setCustomProviderId('');
      setCustomFields([{ key: '', value: '' }]);
      setNewBotToken('');
      setIsVerifyingBot(false);
      setBotVerifyError(null);
    }
  }, [isOpen]);

  const handleSave = async () => {
    setError(null);

    try {
      if (isCreating && isCustom) {
        if (!customProviderId.trim()) {
          setError('Provider ID is required');
          return;
        }
        const customConfig: Record<string, string> = {};
        for (const field of customFields) {
          if (field.key.trim()) {
            customConfig[field.key.trim()] = field.value;
          }
        }
        await createIntegration.mutateAsync({
          provider: customProviderId.trim(),
          name: name || customProviderId.trim(),
          config: customConfig,
        });
      } else if (isCreating && activeProviderId) {
        await createIntegration.mutateAsync({
          provider: activeProviderId,
          name,
          config,
        });
      } else if (integration) {
        await updateIntegration.mutateAsync({
          id: integration.id,
          data: { name, config },
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!integration || !confirm('Are you sure you want to delete this integration?')) {
      return;
    }

    try {
      await deleteIntegration.mutateAsync(integration.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/25" />

      <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
        <Dialog.Panel className="w-screen max-w-md transform bg-white shadow-xl">
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-secondary-200 px-6 py-4">
              <Dialog.Title className="text-lg font-semibold text-secondary-900">
                {showProviderSelection
                  ? 'Choose Provider'
                  : isCreating
                    ? 'Add Integration'
                    : 'Configure Integration'}
              </Dialog.Title>
              <button
                onClick={onClose}
                className="rounded-lg p-2 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-600"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>

            {isCustom ? (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {error && (
                    <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}

                  <div className="space-y-4">
                    <Input
                      label="Provider ID"
                      value={customProviderId}
                      onChange={(e) => setCustomProviderId(e.target.value)}
                      placeholder="e.g. hubspot, stripe, custom_crm"
                    />
                    <Input
                      label="Display Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Integration name"
                    />

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-secondary-700">Configuration</label>
                        <button
                          type="button"
                          onClick={() => setCustomFields([...customFields, { key: '', value: '' }])}
                          className="text-xs text-secondary-500 hover:text-secondary-700 transition-colors"
                        >
                          + Add field
                        </button>
                      </div>
                      <div className="space-y-2">
                        {customFields.map((field, index) => (
                          <div key={index} className="flex gap-2 items-start">
                            <Input
                              value={field.key}
                              onChange={(e) => {
                                const updated = [...customFields];
                                updated[index] = { ...updated[index], key: e.target.value };
                                setCustomFields(updated);
                              }}
                              placeholder="Key"
                              className="flex-1"
                            />
                            <Input
                              value={field.value}
                              onChange={(e) => {
                                const updated = [...customFields];
                                updated[index] = { ...updated[index], value: e.target.value };
                                setCustomFields(updated);
                              }}
                              placeholder="Value"
                              className="flex-1"
                            />
                            {customFields.length > 1 && (
                              <button
                                type="button"
                                onClick={() => setCustomFields(customFields.filter((_, i) => i !== index))}
                                className="mt-2 text-secondary-400 hover:text-secondary-600 transition-colors"
                              >
                                <X className="h-4 w-4" aria-hidden />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between border-t border-secondary-200 px-6 py-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCustom(false);
                      setCustomProviderId('');
                      setCustomFields([{ key: '', value: '' }]);
                      setName('');
                      setError(null);
                    }}
                  >
                    Back
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      isLoading={createIntegration.isPending}
                    >
                      Create
                    </Button>
                  </div>
                </div>
              </>
            ) : showProviderSelection ? (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <div className="space-y-2">
                    {availableProviders.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setSelectedProvider(p.id)}
                        className="w-full flex items-center gap-3 rounded-lg border border-secondary-200 p-3 text-left hover:border-secondary-400 hover:bg-secondary-50 transition-colors"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                          <span className="text-sm font-medium">
                            {p.id.slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-secondary-900">{p.name}</div>
                          <div className="text-xs text-secondary-500">{p.description}</div>
                        </div>
                        <span className="ml-auto shrink-0 text-xs text-secondary-400 bg-secondary-100 px-2 py-0.5 rounded">
                          {p.category}
                        </span>
                      </button>
                    ))}
                    <button
                      onClick={() => setIsCustom(true)}
                      className="w-full flex items-center gap-3 rounded-lg border border-dashed border-secondary-300 p-3 text-left hover:border-secondary-400 hover:bg-secondary-50 transition-colors"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                        <Plus className="h-5 w-5" aria-hidden />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-secondary-900">Custom Integration</div>
                        <div className="text-xs text-secondary-500">Add any service with custom config</div>
                      </div>
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-end border-t border-secondary-200 px-6 py-4">
                  <Button variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {error && (
                    <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}

                  <div className="space-y-4">
                    <Input
                      label="Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Integration name"
                    />

                    {provider === 'google_ads' && (
                      <div className="rounded-lg border border-secondary-200 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-secondary-700">Google OAuth</span>
                          {config.refreshToken ? (
                            <Badge variant="success" dot>Connected</Badge>
                          ) : (
                            <Badge>Not connected</Badge>
                          )}
                        </div>
                        <p className="text-xs text-secondary-500">
                          {config.refreshToken
                            ? 'OAuth credentials are configured. You can reconnect to refresh the token.'
                            : 'Connect your Google account to authorize the Ads API.'}
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            setIsConnectingOAuth(true);
                            try {
                              let id = integration?.id;

                              // Save the integration first so config fields (developerToken, customerId) are persisted
                              if (isCreating && !integration) {
                                const result = await createIntegration.mutateAsync({
                                  provider: 'google_ads',
                                  name: name || 'Google Ads',
                                  config,
                                });
                                id = result.integration.id;
                              } else if (integration) {
                                await updateIntegration.mutateAsync({
                                  id: integration.id,
                                  data: { name, config },
                                });
                              }

                              const { url } = await getGoogleAdsAuthUrl(id);
                              window.location.href = url;
                            } catch (err) {
                              setError(err instanceof Error ? err.message : 'Failed to get auth URL');
                              setIsConnectingOAuth(false);
                            }
                          }}
                          disabled={isConnectingOAuth}
                          className="w-full flex items-center justify-center gap-2 rounded-lg border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-secondary-700 shadow-sm hover:bg-secondary-50 disabled:opacity-50 transition-colors"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                          </svg>
                          {isConnectingOAuth ? 'Redirecting...' : config.refreshToken ? 'Reconnect with Google' : 'Connect with Google'}
                        </button>
                      </div>
                    )}

                    {provider === 'bing_ads' && (
                      <div className="rounded-lg border border-secondary-200 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-secondary-700">Microsoft sign-in</span>
                          {config.refreshToken ? (
                            <Badge variant="success" dot>Connected</Badge>
                          ) : (
                            <Badge>Not connected</Badge>
                          )}
                        </div>
                        <p className="text-xs text-secondary-500">
                          {config.refreshToken
                            ? 'A refresh token is stored and renews itself. Reconnect only if a sync reports invalid_grant.'
                            : 'Fill in the fields below (Client ID and Secret included), then sign in with the Microsoft Advertising user. The Azure app needs this page\'s /oauth/callback URL as a Web redirect URI.'}
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!config.clientId) {
                              setError('Enter the Azure app Client ID first');
                              return;
                            }
                            setIsConnectingOAuth(true);
                            try {
                              let id = integration?.id;
                              // Save first so the Client ID/Secret the callback needs are stored.
                              if (isCreating && !integration) {
                                const result = await createIntegration.mutateAsync({
                                  provider: 'bing_ads',
                                  name: name || 'Microsoft Ads',
                                  config,
                                });
                                id = result.integration.id;
                              } else if (integration) {
                                await updateIntegration.mutateAsync({
                                  id: integration.id,
                                  data: { name, config },
                                });
                              }
                              if (!id) throw new Error('Save the integration first');
                              const { url } = await getBingAdsAuthUrl(id);
                              window.location.href = url;
                            } catch (err) {
                              setError(err instanceof Error ? err.message : 'Failed to get auth URL');
                              setIsConnectingOAuth(false);
                            }
                          }}
                          disabled={isConnectingOAuth}
                          className="w-full flex items-center justify-center gap-2 rounded-lg border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-secondary-700 shadow-sm hover:bg-secondary-50 disabled:opacity-50 transition-colors"
                        >
                          {isConnectingOAuth ? 'Redirecting...' : config.refreshToken ? 'Reconnect Microsoft Advertising' : 'Connect Microsoft Advertising'}
                        </button>
                      </div>
                    )}

                    {provider === 'slack' && (
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-secondary-700">Connected Bots</label>

                        {/* List of connected bots */}
                        {Array.isArray(config.bots) && (config.bots as Array<{ token?: string; appName: string }>).length > 0 ? (
                          <div className="space-y-2">
                            {(config.bots as Array<{ token?: string; appName: string }>).map((bot, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-surface-tertiary text-text-secondary">
                                    <Check className="h-3.5 w-3.5" aria-hidden />
                                  </span>
                                  <span className="text-sm font-medium text-secondary-900">{bot.appName}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const bots = [...(config.bots as Array<{ token?: string; appName: string }>)];
                                    bots.splice(index, 1);
                                    setConfig({ ...config, bots });
                                  }}
                                  className="rounded p-1 text-secondary-400 hover:bg-secondary-200 hover:text-secondary-600 transition-colors"
                                >
                                  <X className="h-4 w-4" aria-hidden />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-secondary-500">No bots connected yet. Add your first bot below.</p>
                        )}

                        {/* Add new bot */}
                        <div className="rounded-lg border border-dashed border-secondary-300 p-3 space-y-2">
                          {botVerifyError && (
                            <Alert variant="error" className="text-xs" onClose={() => setBotVerifyError(null)}>
                              {botVerifyError}
                            </Alert>
                          )}
                          <Input
                            label="Bot Token"
                            type="password"
                            value={newBotToken}
                            onChange={(e) => {
                              setNewBotToken(e.target.value);
                              setBotVerifyError(null);
                            }}
                            placeholder="xoxb-..."
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!newBotToken.trim() || isVerifyingBot}
                            isLoading={isVerifyingBot}
                            onClick={async () => {
                              setIsVerifyingBot(true);
                              setBotVerifyError(null);
                              try {
                                const result = await verifySlackToken(newBotToken.trim());
                                if (!result.ok) {
                                  setBotVerifyError(result.error || 'Invalid bot token');
                                  return;
                                }
                                const bots = [...((config.bots as Array<{ token?: string; appName: string }>) || [])];
                                bots.push({ token: newBotToken.trim(), appName: result.appName || 'Unknown Bot' });
                                setConfig({ ...config, bots });
                                setNewBotToken('');
                              } catch (err) {
                                setBotVerifyError(err instanceof Error ? err.message : 'Failed to verify token');
                              } finally {
                                setIsVerifyingBot(false);
                              }
                            }}
                          >
                            Verify & Add Bot
                          </Button>
                        </div>

                        <div className="rounded-lg border border-dashed border-secondary-300 p-3 space-y-2">
                          <Input
                            label="User Token (for personal DMs)"
                            type="password"
                            value={(config.userToken as string) ?? ''}
                            onChange={(e) => setConfig({ ...config, userToken: e.target.value })}
                            placeholder={
                              integration && (integration.config as Record<string, unknown>).userToken === '••••••••'
                                ? 'Saved — leave empty to keep current value'
                                : 'xoxp-...'
                            }
                            hint="Optional. xoxp-… token used to send review DMs as the installing user (appears under Direct messages, not Apps)."
                          />
                        </div>
                      </div>
                    )}

                    {fields.map((field) => {
                      const isSensitive = field.type === 'password' || field.type === 'textarea';
                      const hasExistingValue = integration && (integration.config as Record<string, unknown>)[field.key] === '••••••••';
                      const placeholder = hasExistingValue && isSensitive
                        ? 'Saved — leave empty to keep current value'
                        : field.type === 'textarea'
                          ? `Paste ${field.label.toLowerCase()}`
                          : `Enter ${field.label.toLowerCase()}`;

                      return field.type === 'textarea' ? (
                        <Textarea
                          key={field.key}
                          label={field.label}
                          hint={field.hint}
                          value={getNestedValue(config, field.key)}
                          onChange={(e) => setConfig(setNestedValue(config, field.key, e.target.value))}
                          placeholder={placeholder}
                          rows={6}
                        />
                      ) : (
                        <Input
                          key={field.key}
                          label={field.label}
                          type={field.type}
                          hint={field.hint}
                          value={getNestedValue(config, field.key)}
                          onChange={(e) => setConfig(setNestedValue(config, field.key, e.target.value))}
                          placeholder={placeholder}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between border-t border-secondary-200 px-6 py-4">
                  <div>
                    {integration ? (
                      <Button
                        variant="danger"
                        onClick={handleDelete}
                        isLoading={deleteIntegration.isPending}
                      >
                        Delete
                      </Button>
                    ) : isCreating && !providerId && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSelectedProvider(null);
                          setConfig({});
                          setName('');
                          setError(null);
                        }}
                      >
                        Back
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      isLoading={createIntegration.isPending || updateIntegration.isPending}
                    >
                      {isCreating ? 'Create' : 'Save'}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
